/**
 * <dd-review-summary> — PDP strip: loads up to 5 customer review photos
 * from the same Klaviyo proxy as <dd-reviews> (review.images URLs only).
 *
 * Attributes: data-product-id, data-api-url (base URL, no trailing slash required)
 */
if (!customElements.get('dd-review-summary')) {
  class DDReviewSummary extends HTMLElement {
    connectedCallback() {
      this._bindScrollToReviews();
      this._loadAvatars();
    }

    /**
     * Intercept clicks on the `.dd-review-summary__hit` anchor so we smooth-
     * scroll to `#reviews` instead of the browser's instant hash jump. Keeps
     * the URL hash in sync (push, not replace) so back-button still works,
     * and respects `prefers-reduced-motion` by falling through to the
     * native jump. `scroll-margin-top` on `.dd-reviews-section` keeps the
     * heading clear of the sticky header.
     */
    _bindScrollToReviews() {
      const hit = this.querySelector('a.dd-review-summary__hit[href="#reviews"]');
      if (!hit) return;
      hit.addEventListener('click', (e) => {
        const target = document.getElementById('reviews');
        if (!target) return;
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        e.preventDefault();
        target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        // Keep a real history entry so the hash is shareable / back-button works.
        if (window.location.hash !== '#reviews') {
          history.pushState(null, '', '#reviews');
        }
      });
    }

    async _loadAvatars() {
      // Defense-in-depth fallback: when the snippet renders without an
      // explicit api URL (older cached HTML, or no theme/block setting
      // configured) we still want avatars. The Liquid snippet has the
      // same default — keeping both in sync means the strip works no
      // matter which renders first.
      const DEFAULT_API = 'https://dd-klaviyo-reviews.vercel.app';
      const api = ((this.getAttribute('data-api-url') || DEFAULT_API).trim() || DEFAULT_API).replace(/\/$/, '');
      const proxyToken = this.getAttribute('data-proxy-token') || '';
      const productId = this.getAttribute('data-product-id');
      const slot = this.querySelector('[data-dd-review-avatars]');
      if (!productId || !slot) return;

      try {
        const url = new URL(`${api}/reviews`);
        url.searchParams.set('product_id', productId);

        const controller = new AbortController();
        const t = setTimeout(() => controller.abort(), 8000);
        const fetcher = window.DDKlaviyoReview?.fetchReviewsProxy || fetch;
        const res = await fetcher(url.toString(), proxyToken, { signal: controller.signal });
        clearTimeout(t);

        if (!res.ok) return;

        const data = await res.json();
        if (data.error) return;

        const reviews = data.reviews || [];
        const urls = [];
        const seen = new Set();

        for (let r = 0; r < reviews.length && urls.length < 5; r++) {
          const imgs = reviews[r].images;
          if (!Array.isArray(imgs)) continue;
          for (let j = 0; j < imgs.length && urls.length < 5; j++) {
            // Proxy returns `{ url, thumb_url }` objects; fall back to bare
            // string for older cached payloads.
            const entry = imgs[j];
            const u = typeof entry === 'string'
              ? entry
              : (entry && (entry.thumb_url || entry.url)) || '';
            if (!u || seen.has(u)) continue;
            seen.add(u);
            urls.push(u);
          }
        }

        if (urls.length === 0) return;

        slot.textContent = '';
        // Stack the first avatar on top, then descend. Default DOM
        // order paints later siblings over earlier ones, so we need an
        // explicit z-index that decreases with index. urls.length is
        // the highest, so the first <img> wins the z-fight.
        urls.forEach((src, i) => {
          const img = document.createElement('img');
          img.className = 'dd-review-summary__avatar';
          img.src = src;
          img.alt = '';
          img.loading = 'lazy';
          img.decoding = 'async';
          img.width = 48;
          img.height = 48;
          img.style.zIndex = String(urls.length - i);
          slot.appendChild(img);
        });
        slot.hidden = false;
      } catch (_) {
        /* keep strip without avatars */
      }
    }
  }

  customElements.define('dd-review-summary', DDReviewSummary);
}
